import React, { useEffect, useState } from 'react';
import { IARTComponentsFormValues } from 'models/ancillaries';
import { useTranslations } from 'hooks/useTranslations';
import { Resolver, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import useComponentsFieldsFormSchema from 'pages/AncillariesPage/AncillariesReferenceTablePage/ARTComponentsForm/schemaValidation';
import { useLang } from 'models/langContext';
import { useAncillaries } from 'pages/AncillariesPage/hooks';
import { EDictionaryTypes } from 'models/dictionary';
import { EArtComponentType } from 'constants/ArtAccessoryType';
import { getFieldsByComponentType } from 'pages/AncillariesPage/helpers';
import Grid from '@mui/material/Grid';
import { SearchWrapper } from 'components/atoms/SearchWrapper';
import { Stack, Typography } from '@mui/material';
import { mapDictionaryToOptionProp } from 'helpers/dictionary';
import { FormInput } from 'components/molecules/FormInput';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonSizesPX, EButtonVariants } from 'constants/Buttons';
import { useLocation, useNavigate } from 'react-router-dom';
import { ERouteLinks } from 'models/route';
import { colors } from 'theme/colors';
import Link from '@mui/material/Link';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { EIconTypes } from 'constants/Icons';
import { useScroll } from 'hooks/useScroll';
import {
  componentTypeForFirearmType,
  ESearchFormLabels,
} from 'pages/AncillariesPage/AncillariesReferenceTablePage/ARTComponentsForm/helpers';
import { IFormElementOptionProp } from 'models/form';
import { usePermission } from 'hooks/usePermission';
import { EPermission } from 'models/permissions';
import { FormWyswigEditor } from 'components/molecules/FormWyswigEditor';
import { FormSearchSelect } from 'components/molecules/FormSearchSelect';
import { FormAsyncAutocomplete } from 'components/molecules/FormAsyncAutocomplete';

const initialFormValues: IARTComponentsFormValues = {
  manufacturersUniqueIds: [],
  calibresUniqueIds: [],
  compositionsUniqueIds: [],
  capacitiesUniqueIds: [],
  proofHousesUniqueIds: [],
  frtMakesUniqueIds: [],
  frtModelsUniqueIds: [],
  frtManufacturersUniqueIds: [],
  frtTypesUniqueIds: [],
  componentTypeUniqueId: '',
  productName: '',
  length: '',
  notes: '',
  frtId: '',
  frtLocal: false,
};

interface IARTComponentsForm {
  onSubmit: (arg: IARTComponentsFormValues) => void;
  onCancel: () => void;
  initialData?: IARTComponentsFormValues;
  isEdit?: boolean;
}

const ARTComponentsForm = ({
  onSubmit,
  onCancel,
  initialData,
  isEdit = false,
}: IARTComponentsForm) => {
  const { t } = useTranslations();
  const navigate = useNavigate();
  const { hasPermissions } = usePermission();
  const { state: routerState }: any = useLocation();
  const { schema } = useComponentsFieldsFormSchema();
  const [accessFields, setAccessFields] = useState<string[]>([]);
  const [selectedFirearm, setSelectedFirearm] = useState<any>(null);
  const { isDesktop } = useThemeBreakpoint();
  const { elRef, scrollToError } = useScroll();
  const [isSubmitLoading, setSubmitLoading] = useState(false);
  const [componentTypeProps, setComponentTypeProps] = useState<IFormElementOptionProp[]>([]);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<IARTComponentsFormValues>({
    defaultValues: isEdit ? initialData : initialFormValues,
    resolver: yupResolver(schema) as unknown as Resolver<IARTComponentsFormValues>,
  });
  const { selectedLanguage } = useLang();
  const firearmType =
    routerState && routerState.firearm && routerState.firearm.type
      ? routerState.firearm.type
      : initialData?.frtTypeUniqueId;

  const {
    loadDictionaries,
    artComponentType,
    artComponentManufacturer,
    artComponentCalibre,
    artComponentCapacity,
    artComponentProofHouse,
    artAccessoryComposition,
  } = useAncillaries();

  useEffect(() => {
    (async () => {
      await loadDictionaries([
        EDictionaryTypes.ArtComponentType,
        EDictionaryTypes.ArtComponentManufacturer,
        EDictionaryTypes.ArtComponentCalibre,
        EDictionaryTypes.ArtComponentCapacity,
        EDictionaryTypes.ArtComponentProofHouse,
        EDictionaryTypes.ArtaccessoryComposition,
      ]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  useEffect(() => {
    setComponentTypeProps(
      mapDictionaryToOptionProp(
        artComponentType?.filter(
          (componentTypeDict) =>
            componentTypeDict.uniqueId &&
            componentTypeForFirearmType(firearmType)
              .map((value) => value.toString())
              .includes(componentTypeDict.uniqueId.toString()),
        ) ?? [],
      ),
    );
  }, [artComponentType, firearmType]);

  useEffect(() => {
    reset(initialFormValues);
    if (isEdit && initialData) {
      const firearm = {
        id: initialData?.frtId,
        makeUniqueId: initialData?.frtMakeUniqueId,
        modelUniqueId: initialData?.frtModelUniqueId,
        manufacturerUniqueId: initialData?.frtManufacturerUniqueId,
        action: '',
        typeUniqueId: initialData?.frtTypeUniqueId,
        country: null,
        isLocal: initialData?.frtLocal,
      };
      setSelectedFirearm(firearm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (routerState?.firearm) {
      setSelectedFirearm(routerState.firearm);
    }
  }, [routerState]);

  useEffect(() => {
    (async () => {
      scrollToError(errors);
    })();
  }, [errors]);

  const submitForm = async (data: IARTComponentsFormValues) => {
    setSubmitLoading(true);
    if (selectedFirearm) {
      if (selectedFirearm?.id) {
        data.frtId = selectedFirearm.id;
      }
      if (selectedFirearm?.isLocal) {
        data.frtLocal = selectedFirearm.isLocal;
      }
      if (selectedFirearm?.makeUniqueId) {
        data.frtMakeUniqueId = selectedFirearm.makeUniqueId;
      }
      if (selectedFirearm?.modelUniqueId) {
        data.frtModelUniqueId = selectedFirearm.modelUniqueId;
      }
      if (selectedFirearm?.manufacturerUniqueId) {
        data.frtManufacturerUniqueId = selectedFirearm.manufacturerUniqueId;
      }
      if (selectedFirearm?.typeUniqueId) {
        data.frtTypeUniqueId = selectedFirearm.typeUniqueId;
      }
    }
    await onSubmit(data);
    setSubmitLoading(false);
  };

  const handleCancel = () => {
    if (routerState?.prevPage) {
      navigate(routerState.prevPage);
    } else {
      onCancel();
    }
  };

  const componentsTypeChange = (value: EArtComponentType | '') => {
    const notes = getValues('notes');
    if (isEdit) {
      reset({ ...initialData, componentTypeUniqueId: value, notes });
    } else {
      reset({ ...initialFormValues, componentTypeUniqueId: value, notes });
    }
    if (value) {
      const fields = getFieldsByComponentType(value);
      setAccessFields(fields);
    } else {
      setAccessFields([]);
    }
  };

  useEffect(() => {
    if (isEdit && initialData?.componentTypeUniqueId) {
      componentsTypeChange(initialData.componentTypeUniqueId as EArtComponentType);
      reset(initialData);
    }
  }, []);

  const checkDisabled = (fieldName: string) => !accessFields.includes(fieldName);

  let frtDetailsTitle = '';
  if (selectedFirearm) {
    const { make, model, action } = selectedFirearm;
    frtDetailsTitle = `${make ? `${make}` : ''}${model ? `, ${model}` : ''}${
      action ? `, ${action}` : ''
    }`;
  }

  const removeFirearmButtonProps = {
    ...(isDesktop ? { icon: EIconTypes.delete } : { label: t('general.delete.text') }),
    ...(isDesktop ? {} : { size: EButtonSizes.small }),
    ...(isDesktop ? {} : { sizePX: EButtonSizesPX.px24 }),
    ...(isDesktop
      ? { variant: EButtonVariants.outlinedIconTertiary }
      : { variant: EButtonVariants.outlined }),
    fullWidth: !isDesktop,
    disabled: isEdit,
  };

  const renderFirearmDetailsActionButtons = (isTemplateFooter: boolean) => (
    <Grid
      container
      size={{ xs: 12 }}
      sx={{
        display: 'flex',
        justifyContent: isTemplateFooter ? 'flex-end' : 'space-between',
        alignItems: 'center',
        borderTop: {
          xs: `1px solid ${colors.primary25}`,
          lg: 'none',
        },
        pt: {
          xs: isTemplateFooter ? 0 : 2,
          lg: 0,
        },
        mt: {
          xs: isTemplateFooter ? 0 : 2,
          lg: 0,
        },
      }}
    >
      {!isDesktop && !isTemplateFooter && (
        <Grid size={{ xs: 6, sm: 'auto' }}>
          <Typography variant="button" sx={{ textTransform: 'uppercase' }}>
            {t('general.actions.button')}:{' '}
          </Typography>
        </Grid>
      )}
      <Grid
        container
        size={{ xs: 8, sm: 6, md: 1 }}
        spacing={2}
        sx={{
          display: {
            xs: isTemplateFooter ? 'none' : 'flex',
            lg: isTemplateFooter ? 'flex' : 'none',
          },
          justifyContent: 'flex-end',
          alignItems: 'center',
        }}
      >
        <Grid
          size={{ xs: 12 }}
          sx={{
            minWidth: { sm: !isTemplateFooter ? 200 : 'auto' },
            maxWidth: { xs: !isTemplateFooter ? 200 : 'auto', sm: 'auto' },
          }}
        >
          <Button
            {...removeFirearmButtonProps}
            onClick={() => {
              setSelectedFirearm(null);
              if (routerState?.firearm) {
                delete routerState.firearm;
              }
              window.history.replaceState({}, document.title);
            }}
          />
        </Grid>
      </Grid>
    </Grid>
  );

  return (
    <form onSubmit={handleSubmit(submitForm)}>
      <div ref={elRef}>
        <Grid container>
          <Grid size={{ xs: 12 }} style={{ marginBottom: '10px' }}>
            <SearchWrapper>
              <Typography variant="h5">
                {t('addAncillary.firearmDetails.section.title.label')}
              </Typography>
            </SearchWrapper>
          </Grid>

          {!isEdit && (
            <Grid sx={{ mb: 2 }}>
              <Button
                fullWidth
                id="ancillary-reference-table_component_select-firearm-reference"
                label={t('ancillaryReferenceTable.component.selectFirearmReference.label')}
                size={EButtonSizes.small}
                variant={EButtonVariants.outlined}
                disabled={isEdit}
                onClick={() =>
                  navigate(`${ERouteLinks.AddAncillariesSearchForFRTFirearm}`, {
                    state: { ...routerState, ancillaries: true },
                  })
                }
              />
            </Grid>
          )}
          {selectedFirearm !== null ? (
            <Grid
              container
              size={{ xs: 12 }}
              sx={{
                border: `1px solid ${colors.primary25}`,
                borderRadius: '4px',
                overflow: 'hidden',
              }}
            >
              <Grid
                container
                size={{ xs: 12 }}
                columns={12}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  backgroundColor: colors.primary100,
                  px: 2,
                  py: 1,
                }}
              >
                <Grid size={{ xs: 11 }}>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {frtDetailsTitle}
                  </Typography>
                </Grid>
                {!isEdit && renderFirearmDetailsActionButtons(false)}
              </Grid>
              <Grid container size={{ xs: 12 }} rowSpacing={2} sx={{ p: 2 }}>
                <Grid size={{ xs: 12, lg: 6 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t('addFirearm.firearmDetails.section.firearmsReferenceNo.label')}
                    </Typography>
                    <Typography variant="body2">
                      {selectedFirearm?.frtId ?? selectedFirearm?.id}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid size={{ xs: 12, lg: 6 }}>
                  {hasPermissions([EPermission.VIEW_FIREARMS]) && (
                    <Link
                      href={ERouteLinks.FRTDetails.replace(
                        ':frtId',
                        routerState?.firearm?.id || selectedFirearm?.id,
                      )}
                      underline="none"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button
                        fullWidth
                        label={t(
                          'addFirearm.firearmDetails.section.showFRTInformation.button',
                        )}
                        variant={EButtonVariants.contained}
                        size={EButtonSizes.small}
                      />
                    </Link>
                  )}
                </Grid>
                {!isEdit && renderFirearmDetailsActionButtons(true)}
              </Grid>
            </Grid>
          ) : null}
        </Grid>

        {selectedFirearm !== null && (
          <Grid container spacing={2} columns={10}>
            <Grid size={{ xs: 12 }} style={{ marginBottom: '10px', marginTop: '20px' }}>
              <SearchWrapper>
                <Typography variant="h5">
                  {t('ancillariesDetails.accordion.title.label')}
                </Typography>
              </SearchWrapper>
            </Grid>
            <Grid size={{ xs: 12, md: 3, sm: 5, lg: 2.5 }}>
              <FormSearchSelect
                options={componentTypeProps}
                name="componentTypeUniqueId"
                label={t(ESearchFormLabels.componentTypeUniqueId)}
                setValueAdditionalBehavior={componentsTypeChange}
                canBeEmpty
                control={control}
                errors={errors}
                onSelect={componentsTypeChange}
                disabled={isEdit || componentTypeProps.length === 0}
                required={true}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3, sm: 5, lg: 2.5 }}>
              <FormAsyncAutocomplete
                dictionaryType={EDictionaryTypes.ArtComponentManufacturer}
                name="manufacturerUniqueId"
                label={t(ESearchFormLabels.manufacturerUniqueId)}
                control={control}
                errors={errors}
                disabled={checkDisabled('manufacturersUniqueIds')}
                required={true}
              />
            </Grid>
            <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
              <FormInput
                charsLimit={120}
                maxLength
                name="productName"
                label={t(ESearchFormLabels.productName)}
                control={control}
                errors={errors}
                setValue={setValue}
                disabled={checkDisabled('productName')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3, sm: 5, lg: 2.5 }}>
              <FormAsyncAutocomplete
                dictionaryType={EDictionaryTypes.ArtComponentCalibre}
                name="calibreUniqueId"
                label={t(ESearchFormLabels.calibreUniqueId)}
                control={control}
                errors={errors}
                disabled={checkDisabled('calibresUniqueIds')}
                required={true}
              />
            </Grid>
            <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
              <FormInput
                charsLimit={120}
                maxLength
                name="length"
                label={t(ESearchFormLabels.length)}
                control={control}
                errors={errors}
                setValue={setValue}
                disabled={checkDisabled('length')}
                required={true}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3, sm: 5, lg: 2.5 }}>
              <FormSearchSelect
                options={mapDictionaryToOptionProp(artAccessoryComposition)}
                name="compositionUniqueId"
                label={t(ESearchFormLabels.compositionUniqueId)}
                canBeEmpty
                control={control}
                errors={errors}
                disabled={checkDisabled('compositionsUniqueIds')}
                required={true}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3, sm: 5, lg: 2.5 }}>
              <FormSearchSelect
                options={mapDictionaryToOptionProp(artComponentCapacity)}
                name="capacityUniqueId"
                label={t(ESearchFormLabels.capacityUniqueId)}
                canBeEmpty
                control={control}
                errors={errors}
                disabled={checkDisabled('capacitiesUniqueIds')}
                required={true}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3, sm: 5, lg: 2.5 }}>
              <FormSearchSelect
                options={mapDictionaryToOptionProp(artComponentProofHouse)}
                name="proofHouseUniqueId"
                label={t(ESearchFormLabels.proofHouseUniqueId)}
                canBeEmpty
                control={control}
                errors={errors}
                disabled={checkDisabled('proofHousesUniqueIds')}
              />
            </Grid>
            <Grid size={{ xs: 10 }}>
              <FormWyswigEditor
                name="notes"
                label={t(ESearchFormLabels.notes)}
                setValue={setValue}
                control={control}
                errors={errors}
                disabled={checkDisabled('notes')}
                setError={setError}
                clearErrors={clearErrors}
                charsLimit={5000}
                fullContainer
              />
            </Grid>

            <Grid size={{ xs: 10, sm: 10, md: 6, lg: 5 }} sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }} sx={{ pr: '6px' }}>
                  <Button
                    type="submit"
                    fullWidth
                    label={t('general.save.button')}
                    id="save-button"
                    variant={EButtonVariants.contained}
                    isLoading={isSubmitLoading}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }} sx={{ pl: '3px', pr: '6px' }}>
                  <Button
                    fullWidth
                    label={t('general.cancel.button')}
                    id="cancel-button"
                    variant={EButtonVariants.outlined}
                    onClick={handleCancel}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid size={{ xs: 10 }}>
              <Typography variant="caption">{t('general.mandatoryField.text')}</Typography>
            </Grid>
          </Grid>
        )}
      </div>
    </form>
  );
};

export default ARTComponentsForm;
